import { useCallback, useRef } from 'react';
import { type PointerModifierState } from '@/storybook/components/FormCollection/utils/utils';

type UseMultiSelectHandlersParams<T> = {
  value: T[];
  disabled: boolean;
  onChange: (value: T[]) => void;
};

export function useMultiSelectHandlers<T>({ value, disabled, onChange }: UseMultiSelectHandlersParams<T>) {
  const lastClickedIndexRef = useRef<number>(-1);

  const isSelected = useCallback((selectedValue: T) => value.includes(selectedValue), [value]);

  const toggleOption = useCallback(
    (selectedValue: T) => {
      if (isSelected(selectedValue)) return onChange(value.filter(valueListItem => valueListItem !== selectedValue));
      return onChange([...value, selectedValue]);
    },
    [isSelected, onChange, value],
  );

  const handleClick = useCallback(
    (selectedValue: T, options: T[], modifiers: PointerModifierState) => {
      if (disabled) return;

      const selectedIndex = options.indexOf(selectedValue);

      if (!modifiers.shiftKey) {
        lastClickedIndexRef.current = selectedIndex;
        return toggleOption(selectedValue);
      }

      // Shift+Click: select range between last clicked and current
      if (selectedIndex === -1) return;

      const anchorIndex = lastClickedIndexRef.current;
      if (anchorIndex === -1) {
        lastClickedIndexRef.current = selectedIndex;
        return toggleOption(selectedValue);
      }

      const rangeStart = Math.min(anchorIndex, selectedIndex);
      const rangeEnd = Math.max(anchorIndex, selectedIndex);
      const rangeValues = options.slice(rangeStart, rangeEnd + 1);

      const selecting = !isSelected(selectedValue);
      let newValue: T[];
      if (selecting) {
        const valueSet = new Set(value);
        const toAdd = rangeValues.filter(optionValue => !valueSet.has(optionValue));
        newValue = [...value, ...toAdd];
      } else {
        const removeSet = new Set(rangeValues);
        newValue = value.filter(valueListItem => !removeSet.has(valueListItem));
      }

      onChange(newValue);
    },
    [disabled, toggleOption, isSelected, value, onChange],
  );

  return { handleClick };
}
